<template lang="pug">
.p-trend-chart(ref='chartHost')
</template>

<script>
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';

export default {
  name: 'ElementTrendChart',
  props: {
    /**
     * 時系列データ。各要素は [epoch_seconds, der]
     * @type {Array<[number, number]>}
     */
    data: {
      type: Array,
      required: true,
    },
    width: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    yMin: {
      type: Number,
      default: null,
    },
    yMax: {
      type: Number,
      default: null,
    },
    xMin: {
      type: Number,
      default: null,
    },
    xMax: {
      type: Number,
      default: null,
    },
    yLog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chart: null,
      tooltipEl: null,
    };
  },
  watch: {
    data() {
      if (this.chart) {
        this.chart.setData(this.toUPlotData(this.data));
      }
    },
    width() {
      if (this.chart) {
        this.chart.setSize({ width: this.width, height: this.height });
      }
    },
    height() {
      if (this.chart) {
        this.chart.setSize({ width: this.width, height: this.height });
      }
    },
    yMin() {
      this.refreshYScale();
    },
    yMax() {
      this.refreshYScale();
    },
    xMin() {
      this.refreshXScale();
    },
    xMax() {
      this.refreshXScale();
    },
    yLog() {
      this.rebuildChart();
    },
  },
  mounted() {
    this.$refs.chartHost.style.position = 'relative';
    this.tooltipEl = document.createElement('div');
    this.tooltipEl.className = 'p-trend-chart__tooltip';
    this.$refs.chartHost.appendChild(this.tooltipEl);
    this.createChart();
  },
  beforeDestroy() {
    this.destroyChart();
    if (this.tooltipEl && this.tooltipEl.parentNode) {
      this.tooltipEl.parentNode.removeChild(this.tooltipEl);
    }
    this.tooltipEl = null;
  },
  methods: {
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    rebuildChart() {
      this.destroyChart();
      this.createChart();
    },
    refreshYScale() {
      if (!this.chart) return;
      const ys = this.chart.data && this.chart.data[1];
      if (!ys || ys.length === 0) return;
      let dMin = Infinity;
      let dMax = -Infinity;
      for (let i = 0; i < ys.length; i += 1) {
        const v = ys[i];
        if (v == null || isNaN(v)) continue;
        if (v < dMin) dMin = v;
        if (v > dMax) dMax = v;
      }
      if (!isFinite(dMin)) return;
      const lo = this.yMin != null ? this.yMin : dMin;
      const hi = this.yMax != null ? this.yMax : dMax;
      this.chart.setScale('y', { min: lo, max: hi });
    },
    refreshXScale() {
      if (!this.chart) return;
      const xs = this.chart.data && this.chart.data[0];
      if (!xs || xs.length === 0) return;
      let dMin = Infinity;
      let dMax = -Infinity;
      for (let i = 0; i < xs.length; i += 1) {
        const v = xs[i];
        if (v == null || isNaN(v)) continue;
        if (v < dMin) dMin = v;
        if (v > dMax) dMax = v;
      }
      if (!isFinite(dMin)) return;
      const lo = this.xMin != null ? this.xMin : dMin;
      const hi = this.xMax != null ? this.xMax : dMax;
      this.chart.setScale('x', { min: lo, max: hi });
    },
    createChart() {
      const HOVER_RADIUS_PX = 8;
      const updateTooltip = (u) => {
        const idx = u.cursor.idx;
        if (idx == null || idx < 0) {
          this.tooltipEl.style.display = 'none';
          return;
        }
        const x = u.data[0][idx];
        const y = u.data[1][idx];
        if (x == null || y == null) {
          this.tooltipEl.style.display = 'none';
          return;
        }
        const px = u.valToPos(x, 'x');
        const py = u.valToPos(y, 'y');
        const dx = u.cursor.left - px;
        const dy = u.cursor.top - py;
        if (dx * dx + dy * dy > HOVER_RADIUS_PX * HOVER_RADIUS_PX) {
          this.tooltipEl.style.display = 'none';
          return;
        }
        this.tooltipEl.innerHTML =
          `<div class="p-trend-chart__tooltip-date">${this.formatDate(x)}</div>` +
          `<div class="p-trend-chart__tooltip-value">` +
          `<span class="p-trend-chart__tooltip-num">${y.toFixed(4)}</span>` +
          ` <span class="p-trend-chart__tooltip-unit">μSv/h</span>` +
          `</div>`;
        this.tooltipEl.style.display = 'block';
        const tipW = this.tooltipEl.offsetWidth;
        const tipH = this.tooltipEl.offsetHeight;
        const hostW = this.$refs.chartHost.clientWidth;
        const hostH = this.$refs.chartHost.clientHeight;
        let left = u.cursor.left + 14;
        let top = u.cursor.top - tipH - 14;
        if (left + tipW > hostW) left = u.cursor.left - tipW - 14;
        if (left < 0) left = Math.max(0, hostW - tipW);
        if (top < 0) top = u.cursor.top + 14;
        if (top + tipH > hostH) top = Math.max(0, hostH - tipH);
        this.tooltipEl.style.left = left + 'px';
        this.tooltipEl.style.top = top + 'px';
      };

      const yScale = {};
      if (this.yMin != null || this.yMax != null) {
        yScale.auto = false;
        yScale.range = (u, dataMin, dataMax) => [
          this.yMin != null ? this.yMin : dataMin,
          this.yMax != null ? this.yMax : dataMax,
        ];
      } else {
        yScale.auto = true;
      }
      if (this.yLog) {
        yScale.distr = 3;
      }

      const xScale = { time: true };
      if (this.xMin != null || this.xMax != null) {
        xScale.auto = false;
        xScale.range = (u, dataMin, dataMax) => [
          this.xMin != null ? this.xMin : dataMin,
          this.xMax != null ? this.xMax : dataMax,
        ];
      } else {
        xScale.auto = true;
      }

      const pad2 = (n) => String(n).padStart(2, '0');
      const xAxisValues = (u, splits, axisIdx, foundSpace, foundIncr) => {
        const showDate = foundIncr >= 3600 * 24;
        return splits.map((s) => {
          const d = new Date(s * 1000);
          if (showDate) return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
          return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
        });
      };

      const opts = {
        width: this.width,
        height: this.height,
        cursor: { drag: { x: false, y: false } },
        scales: {
          x: xScale,
          y: yScale,
        },
        axes: [{ values: xAxisValues }, { label: 'Air dose rate (μSv/h)' }],
        series: [
          { label: '日時' },
          {
            label: '線量率',
            stroke: '#3f6df5',
            paths: () => null,
            points: { show: true, size: 4, stroke: '#3f6df5', fill: '#3f6df5' },
          },
        ],
        hooks: {
          setCursor: [updateTooltip],
        },
      };
      this.chart = new uPlot(opts, this.toUPlotData(this.data), this.$refs.chartHost);
    },
    toUPlotData(data) {
      const xs = new Array(data.length);
      const ys = new Array(data.length);
      for (let i = 0; i < data.length; i++) {
        xs[i] = data[i][0];
        ys[i] = data[i][1];
      }
      return [xs, ys];
    },
    formatDate(epochSec) {
      const d = new Date(epochSec * 1000);
      const p = (n) => String(n).padStart(2, '0');
      return (
        `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
        `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
      );
    },
  },
};
</script>

<style lang="postcss">
.p-trend-chart
  width: 100%
  height: 100%
.p-trend-chart__tooltip
  position: absolute
  pointer-events: none
  background: #fff
  color: #222
  padding: 8px 12px
  border: 1px solid #d0d4db
  border-radius: 4px
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
  font-size: 12px
  line-height: 1.4
  white-space: nowrap
  display: none
  z-index: 10
.p-trend-chart__tooltip-date
  color: #667085
  font-size: 11px
  margin-bottom: 2px
.p-trend-chart__tooltip-value
  font-size: 14px
  font-weight: 600
.p-trend-chart__tooltip-num
  font-variant-numeric: tabular-nums
  color: #3f6df5
.p-trend-chart__tooltip-unit
  color: #667085
  font-weight: 400
  font-size: 11px
  margin-left: 2px
</style>
